<template lang="pug">
    div(:class="{'material-input wrapper':isMaterial}")
        input(
            :aria-label="label"
            ref='input-box'
            :type='type'
            :name="name"
            :placeholder="placeholder"
            :required="isRequired"
            :disabled="isDisabled"
            :maxlength="maxlengthComputed"
            :defaultErrorText="defaultErrorText"
            data-role="none",
            @blur="inputBlurEvents"
            v-model="internalValue"
            v-bind="$attrs"
            @focus="onFocus"
            class="baseInput p-3 h-10 w-full max-w-full border rounded"
            :class="classObject"
        )
        material(
            v-if="isMaterial"
            :label="label"
            :isRequired="isRequired"
            :error="error"
            :isDirty="isDirty"
            :disablePosValidation="disablePosValidation"
        )
</template>

<script>
/**
 * A basic Input Box.
 * Handles central logic for all Input types.
 */
const material = () => import(/* webpackChunkName: "material" */ '../material')

export default {
    name: 'BaseInput',
    components: {
        material,
    },
    // inject() allows us to communicate with the Form wrapper component.
    // Injections are only optional if they have a default value
    inject: ['$form'],
    inheritAttrs: false,
    props: {
        // Input Type
        type: {
            type: String,
            default: 'text',
            validator: (value) => ['text', 'email', 'number', 'tel'].includes(value),
        },
        // Not used here but needed to set the css -hasLabel class (for Material styling)
        label: {
            type: String,
            required: false,
            default: null,
        },
        // Name of the input. Also the Key used for Error Handling.
        name: {
            type: String,
            required: true,
        },
        // The value to V-Model too. We use a computed internalValue to map to this Prop.
        value: {
            type: [String, Number],
            default: null,
        },
        placeholder: {
            type: String,
            required: false,
            default: ' ', // Required for the CSS to shift label up and down
        },
        // The error to display if we don't have a specific error to render.
        defaultErrorText: {
            type: String,
            default: null,
        },
        // Is this input required or optional?
        isRequired: {
            type: Boolean,
            default: false,
        },
        // Should this input be disabled?
        isDisabled: {
            type: Boolean,
            default: false,
        },
        // Cap inputs (native html validation)
        maxlength: {
            type: Number,
            default: null,
        },
        // Allow a minlength (validated with js)
        minlength: {
            type: Number,
            default: null,
        },
        isMaterial: {
            type: Boolean,
            default: true,
        },
        disablePosValidation: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            // Toggles showing the error message
            isValid: true,
            isDirty: false, // Becomes dirty when someone touches it
        }
    },
    computed: {
        error() {
            return this.$form.errors[this.name]
        },
        // Determines basic styling classes
        classObject() {
            return {
                '-hasLabel': this.label != null,
                '-hasError': typeof this.error !== 'undefined',
                '-disabled': this.isDisabled,
            }
        },
        // Used to validate the Max Length of the input
        maxlengthComputed() {
            return this.maxlength || 50
        },
        // Maps an internal value to the Value Prop
        internalValue: {
            get() {
                if (this.value && typeof this.value === 'string') return this.value.trim()
                return this.value
            },
            set(newVal) {
                if (newVal) newVal = newVal.trim()
                this.$emit('input', newVal)
            },
        },
    },
    mounted() {
        this.$form.register(this.name, this.validate)
    },
    destroyed() {
        this.$form.unregister(this.name)
    },
    methods: {
        /**
         * Basic input validation
         * @returns {Boolean}
         */
        validate() {
            const input = this.$refs['input-box']
            // Edge case - if the User hits 'enter' on some forms that trigger redirects the forms validation will be called twice (blur and submit)
            // and if it passes the first time the redirect will trigger before the second validation will finish so this.$refs['input-box']  may no longer exist.
            if (typeof input === 'undefined' || input.tagName.toLowerCase() !== 'input') return

            const val = this.internalValue

            // We know this input has been touched.
            this.isDirty = true
            // Tracks the current state of our input
            this.isValid = true
            // Early out if no input and not required
            if (!this.isRequired && val == null) return true

            // Check if it is required, passes HTML5 validation, and any Extended component validation.
            if (this.isRequired && (val == null || val.length <= 0)) {
                this.isValid = false
            } else if (!input.checkValidity()) {
                this.isValid = false
            } else if (this.minlength && val.length < this.minlength) {
                this.isValid = false
            } else if (val.length > 0 && !this.extendedValidator()) {
                this.isValid = false
            }

            // Tells the Form wrapper component about our isValid state
            if (!this.isValid) this.$form.addError(this.name, this.defaultErrorText)
            else this.$form.clearError(this.name)

            // Allows a parent calling this via $ref to act upon our isValid state
            return this.isValid
        },
        // Extending component can overwrite this function to add their own additional checks
        // Example: Email / Phone / FullName  etc
        extendedValidator() {
            return true
        },
        onFocus() {
            this.$emit('onFocus')
        },
        inputBlurEvents() {
            this.validate()
            this.$emit('onBlur')
        },
    },
}
</script>
